/**
 * Firebase Web SDK 초기화 — 클라이언트 전용.
 *
 * Firebase config 7개 값은 모두 공개 가능 (spec §12.1). 진짜 보호는:
 *   - Firebase Console → Authentication → Authorized domains
 *   - Firestore Security Rules
 *   - 백엔드 프록시 (Cloudflare Workers) 의 ID token 검증
 */

import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getAuth,
  type Auth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  onIdTokenChanged,
  type User,
} from 'firebase/auth'

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || undefined,
}

let app: FirebaseApp | null = null
let auth: Auth | null = null

export function getFirebaseApp(): FirebaseApp {
  if (!app) app = initializeApp(config)
  return app
}

export function getFirebaseAuth(): Auth {
  if (!auth) auth = getAuth(getFirebaseApp())
  return auth
}

/**
 * Google 로그인 — 팝업 방식. spec §3.1 FR-01.
 * 성공 시 ID token이 Firebase Auth가 자동으로 갱신해주는 user.getIdToken() 으로 접근.
 */
export async function signInWithGoogle(): Promise<User> {
  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
  const result = await signInWithPopup(getFirebaseAuth(), provider)
  return result.user
}

export async function signOut(): Promise<void> {
  await fbSignOut(getFirebaseAuth())
}

/**
 * ID token 변화 구독 — 토큰 만료 30초 전쯤 Firebase가 자동 갱신.
 */
export function subscribeIdToken(cb: (user: User | null) => void): () => void {
  return onIdTokenChanged(getFirebaseAuth(), cb)
}

/**
 * 현재 사용자의 ID token 한 번 받아오기. 백엔드 호출에 `Authorization: Bearer {token}` 으로 사용.
 * forceRefresh=true 면 만료가 임박했더라도 강제 갱신.
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = getFirebaseAuth().currentUser
  if (!user) return null
  return user.getIdToken(forceRefresh)
}
